import React, { useCallback, useEffect, useState } from 'react';

import { Box, styled, useMediaQuery, useTheme } from '@mui/material';

import { TableLoadingLayout } from '../layouts/LoadingLayouts/TableLoadingLayout';
import { CartProductsTable } from './cart-product-table/CartProductsTable';
import MobileProductsList from './cart-product-table/MobileProductsList';
import { CartSummaries } from './cart-summary/CartSummaries';

import useShopifyCart from 'hooks/shopify/useShopifyCart';
import useCart from '../../hooks/cart/useCart';
import useProductsPriceSWR from '../../hooks/products/useProductsPriceSWR';

import type {
    CartItemChangeProps,
    CartLineItem,
    CartPageProps,
} from '../../models/Cart';

import WarningButton from 'components/shared/Buttons/WarningButton';
import { useTranslation } from 'react-i18next';
import { getShopifyID } from '../../utils/utils';

const StyledCartWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '15px',
    [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
    },
}));

export const CartPage: React.FC<CartPageProps> = (props) => {
    const { cart, refetch } = props;

    const { t } = useTranslation('cart', {
        keyPrefix: 'CART',
    });

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [cartItems, setCartItems] = useState<CartLineItem[]>([]);

    const [isUpdatingCart, setUpdatingCart] = useState<boolean>(false);

    const shopifyCart = useShopifyCart();

    const cartHook = useCart();

    const { prices, isLoading, extractCustomPrices } = useProductsPriceSWR(
        cartHook.convertProductsIdsFromCart(cart.lines.edges)
    );

    const getProducts = useCallback((): CartLineItem[] => {
        // adding custom prices to products if they have any
        return cart.lines.edges.map((elem) => {
            const customs = extractCustomPrices(
                prices,
                getShopifyID(elem.node.merchandise.product.id),
                getShopifyID(elem.node.merchandise.id)
            );

            if (!customs) return elem.node;

            return {
                ...elem.node,
                customs,
            };
        });
    }, [cart, prices]);

    const handleRemoveCartItem = useCallback(
        (id: string): void => {
            setUpdatingCart(true);
            shopifyCart
                .removeCartItem([id])
                .then(() => {
                    refetch();
                })
                .finally(() => {
                    setUpdatingCart(false);
                });
        },
        [shopifyCart]
    );

    const handleUpdateCartItem = useCallback(
        (id: string, quantity: number): void => {
            setUpdatingCart(true);
            shopifyCart
                .updateCartItem([
                    {
                        id: id,
                        quantity: quantity,
                    },
                ])
                .then(() => {
                    refetch();
                })
                .finally(() => {
                    setUpdatingCart(false);
                });
        },
        [shopifyCart]
    );

    const handleChange = useCallback(
        ({ remove, rowID, qty }: CartItemChangeProps): void => {
            if (remove) return handleRemoveCartItem(rowID);
            handleUpdateCartItem(rowID, qty);
        },
        []
    );

    useEffect((): void => {
        if (isLoading || isUpdatingCart) return;
        setCartItems(getProducts());
    }, [isLoading, isUpdatingCart]);

    return (
        <>
            <StyledCartWrapper>
                {isLoading && <TableLoadingLayout />}
                {!isLoading && (
                    <>
                        <Box sx={{ flex: '70% 0 0' }}>
                            <Box textAlign={'end'} marginBottom={'10px'}>
                                <WarningButton
                                    disabled={
                                        cartItems.length < 1 ||
                                        cartHook.resettingCart
                                    }
                                    onClick={() =>
                                        cartHook.resetCart(
                                            cartItems.map((node) => ({ node }))
                                        )
                                    }
                                >
                                    {t('CLEAR_CART')}
                                </WarningButton>
                            </Box>
                            {isMobile ? (
                                <MobileProductsList
                                    items={cartItems}
                                    updatingCart={isUpdatingCart}
                                    handleChange={handleChange}
                                />
                            ) : (
                                <CartProductsTable
                                    cartItems={cartItems}
                                    updatingCart={isUpdatingCart}
                                    handleChange={handleChange}
                                />
                            )}
                        </Box>
                        <Box sx={{ flex: '30% 0 0' }}>
                            <CartSummaries
                                cart={cart}
                                customPrices={prices}
                                cartItems={cartItems}
                            />
                        </Box>
                    </>
                )}
            </StyledCartWrapper>
        </>
    );
};
